import React, { SyntheticEvent, useContext, useState } from "react";

import {
  Alert,
  Button,
  CircularProgress,
  Grid,
  Snackbar,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { SubscriptionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import SubscriptionAction from "./subscription-action";
import { syncPaymentMethod } from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { DEFAULT_SNACKBAR_DURATION, EventTypeReason } from "constants/index";
import { OrgDataContext } from "context/organisation-context";
import { SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";
import { getBillingProviderName } from "helpers/subscriptions";
import { UrlParamTypes } from "types/org-display";

const SyncPaymentMethod = ({
  refreshOrgData,
}: {
  refreshOrgData: () => void;
}) => {
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);

  const { orgData } = useContext(OrgDataContext);
  const { showError } = useContext(SnackbarContext);

  const subscription = orgData.subscription[0];
  const billingProviderName = getBillingProviderName(subscription);

  const isFreeOrg =
    subscription &&
    (subscription.type === SubscriptionType.FREE ||
      subscription.type === SubscriptionType.FREE_ORG);

  const handleSubmit = () => {
    setShowLoadingSpinner(true);
    return syncPaymentMethod(
      {
        organizationId: toUUID(roleId),
      },
      callOptionsHeaders(regionCode, roleId)
    )
      .then(() => {
        setOpenSuccessAlert(true);
        refreshOrgData();
      })
      .catch(showError)
      .finally(() => {
        setShowLoadingSpinner(false);
      });
  };

  const handleClose = (
    event?: Event | SyntheticEvent<unknown, Event>,
    reason?: string | undefined
  ) => {
    if (reason === EventTypeReason.Clickaway) {
      return;
    }
    setOpenSuccessAlert(false);
  };

  return (
    <React.Fragment>
      <SubscriptionAction title={"Sync Payment Method:"}>
        {showLoadingSpinner ? (
          <Grid container justifyContent="center" sx={{ mt: 1, mr: 2 }}>
            <CircularProgress
              color="primary"
              variant="indeterminate"
              thickness={4}
              size={30}
            />
          </Grid>
        ) : (
          <Button
            sx={{ mt: 1, mr: 2 }}
            variant="contained"
            color="primary"
            fullWidth
            disabled={isFreeOrg}
            onClick={() => setOpenConfirmDialog(true)}
            data-testid="sync-payment-method-btn"
          >
            SYNC PAYMENT METHOD
          </Button>
        )}
      </SubscriptionAction>

      <ConfirmDialog
        title="Sync Payment Method"
        open={openConfirmDialog}
        width={"md"}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
        setOpen={setOpenConfirmDialog}
        onConfirm={() => handleSubmit()}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography data-testid="sync-payment-method-dialog-text">
              This will sync the default payment method from{" "}
              {billingProviderName} to the SafetyCulture platform if it exists.
              <br />
              Are you sure you want to <b>sync</b> payment method from{" "}
              {billingProviderName}?
            </Typography>
          </Grid>
        </Grid>
      </ConfirmDialog>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={openSuccessAlert}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={handleClose}
      >
        <Alert severity="success" variant="filled">
          Payment method synced
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
};

export default SyncPaymentMethod;
